/**
 * Staging expand and collapse, so the chart reads as one movement rather than
 * two things happening at once.
 *
 * Every position comes from `layoutOrg`, and React Flow animates a node from
 * wherever it was to wherever the layout now says. That works for a card that
 * exists in both layouts and does nothing at all for one that has just
 * appeared: a manager's reports were being mounted straight at their final
 * positions, so expanding looked like a pop rather than a reveal, while
 * collapsing looked animated only because the survivors slid.
 *
 * The answer is two frames. On expand the manager slides aside first with its
 * reports parked invisibly behind it, and only then do the reports fly out. On
 * collapse the reports retract into the card before anything else moves. This
 * module works out which nodes those are and where they should be parked; the
 * chart plays the two frames.
 */

/** What kind of movement this layout change is, and where the new cards start. */
export type Choreography =
  | { kind: 'none' }
  /** Ids appearing, each mapped to the id they should emerge from behind. */
  | { kind: 'expand'; parked: Map<string, string> }
  /** Ids leaving, each mapped to the id they should retract into. */
  | { kind: 'collapse'; parked: Map<string, string> };

const NONE: Choreography = { kind: 'none' };

/**
 * Work out how to play the move from one set of visible cards to another.
 *
 * Only a change that is purely arrivals or purely departures is staged. A
 * layout where both happen at once is not an expand or a collapse — it is a
 * different chart, from switching scenario or focus — and choreographing it
 * would mean animating cards out of a card that is itself on its way somewhere.
 */
export function choreograph(
  previous: readonly string[],
  next: readonly string[],
  managerOf: (id: string) => string | null | undefined,
): Choreography {
  // Nothing to stage before there is a chart, and nothing to stage when the
  // set has not moved — a re-parent inside one layout animates on its own.
  if (previous.length === 0 || next.length === 0) return NONE;

  const before = new Set(previous);
  const after = new Set(next);

  const entering = next.filter((id) => !before.has(id));
  const leaving = previous.filter((id) => !after.has(id));

  if (entering.length > 0 && leaving.length > 0) return NONE;
  if (entering.length === 0 && leaving.length === 0) return NONE;

  /*
   * The card a new one should come out from behind is its nearest ancestor that
   * was already on screen — which is the manager that was expanded, except for
   * the reports of reports that arrive with it. Walking up rather than assuming
   * the direct manager is what lets a whole subtree emerge from one card.
   */
  const anchorIn = (id: string, present: ReadonlySet<string>): string | null => {
    const seen = new Set<string>([id]);
    let current = managerOf(id);
    while (current && !seen.has(current)) {
      if (present.has(current)) return current;
      seen.add(current);
      current = managerOf(current);
    }
    return null;
  };

  if (entering.length > 0) {
    const parked = new Map<string, string>();
    for (const id of entering) {
      const anchor = anchorIn(id, before);
      if (anchor) parked.set(id, anchor);
    }
    // An arrival with no ancestor on screen is a new root, not a reveal.
    return parked.size === entering.length ? { kind: 'expand', parked } : NONE;
  }

  const parked = new Map<string, string>();
  for (const id of leaving) {
    const anchor = anchorIn(id, after);
    if (anchor) parked.set(id, anchor);
  }
  return parked.size === leaving.length ? { kind: 'collapse', parked } : NONE;
}

/**
 * How long the first frame is given before the second one starts.
 *
 * Shorter than the 340ms slide on purpose: the two overlap, which is what makes
 * the whole thing read as one movement instead of two steps with a pause in the
 * middle.
 */
export const STAGE_MS = 170;

/** Whether this browser has been asked not to animate things. */
export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * Ids in the order they were first seen, so a card never changes place in the
 * DOM once it has one.
 *
 * This is what makes the animation work at all, and it is not obvious. React
 * reorders keyed children by *moving* DOM elements, and re-inserting an element
 * cancels any transition running on it — so a card whose slot changed in the
 * same commit as its position jumped straight to the new position instead of
 * sliding. Which cards those were depended on the direction of the permutation,
 * which is why expanding used to teleport the manager while its peers slid, and
 * collapsing did the exact opposite.
 *
 * Draw order is free to be arbitrary here because cards in this layout do not
 * overlap: the only ones that share a position are parked behind their manager,
 * and those are invisible.
 */
export function rememberOrder(seen: readonly string[], ids: readonly string[]): string[] {
  const known = new Set(seen);
  const fresh = ids.filter((id) => !known.has(id));
  return fresh.length > 0 ? [...seen, ...fresh] : (seen as string[]);
}

/** `ids`, arranged by `seen`. Anything unknown to `seen` goes last, in its own order. */
export function inStableOrder(seen: readonly string[], ids: readonly string[]): string[] {
  const wanted = new Set(ids);
  const placed = seen.filter((id) => wanted.has(id));
  const known = new Set(placed);
  return [...placed, ...ids.filter((id) => !known.has(id))];
}
